export const DIRECT_SCENE_PROFILE_VERSION = 1 as const;
export const DIRECT_SCENE_PROFILE_JSON_MAX_BYTES = 16 * 1024;

/**
 * Bao nhiêu telemetry còn được đẩy qua MQTT/gateway trong một run.
 *  - `backend`                 : tất cả, như mission thường.
 *  - `bootstrap-normal-finish` : chỉ 3 khung A / B đầu / Z, đủ để backend mở và
 *                                đóng bản ghi `/sim`.
 *  - `direct-only`             : Không có msg nào. Toàn bộ đi direct-message; run
 *                                không để lại dấu vết nào phía backend.
 */
export type DirectTelemetryPolicy = 'backend' | 'bootstrap-normal-finish' | 'direct-only';

export interface DirectSceneProfile {
  version: 1;
  profileId: string;
  missionFamily: string;
  board: {
    widthMm: number;
    lengthMm: number;
    thicknessMm: number;
    sideColor: number;
    topTexturePath: string;
    fallbackColor: number;
  };
  capabilities: {
    pose: boolean;
    rgb: boolean;
    gripper: boolean;
    finish: boolean;
    runtime: boolean;
    objects: boolean;
  };
  telemetryPolicy: DirectTelemetryPolicy;
}

const PROFILE_KEYS = [
  'version',
  'profileId',
  'missionFamily',
  'board',
  'capabilities',
  'telemetryPolicy',
] as const;
const BOARD_KEYS = [
  'widthMm',
  'lengthMm',
  'thicknessMm',
  'sideColor',
  'topTexturePath',
  'fallbackColor',
] as const;
const CAPABILITY_KEYS = [
  'pose',
  'rgb',
  'gripper',
  'finish',
  'runtime',
  'objects',
] as const;
const FORBIDDEN_OPAQUE_IDS = new Set(['__proto__', 'constructor', 'prototype']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expectedKeys: readonly string[],
): boolean {
  const keys = Object.keys(value);
  return (
    keys.length === expectedKeys.length &&
    keys.every(key => expectedKeys.includes(key))
  );
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

export function isDirectOpaqueId(value: unknown): value is string {
  if (
    typeof value !== 'string' ||
    value.length < 1 ||
    value.length > 128 ||
    !/^[A-Za-z0-9_.:-]+$/.test(value)
  )
    return false;

  return !FORBIDDEN_OPAQUE_IDS.has(value.toLowerCase());
}

export function isDirectMissionAssetPath(value: unknown): value is string {
  if (
    typeof value !== 'string' ||
    value.length < 1 ||
    value.length > 512 ||
    !/^\/VirtualLeanbot-Runner\/mission-assets\/[A-Za-z0-9_.-]+(?:\/[A-Za-z0-9_.-]+)*\.(?:png|jpe?g|webp)$/.test(
      value,
    )
  )
    return false;

  return !value.split('/').some(segment => segment === '..' || segment === '.');
}

export function directSceneProfileMatchesMission(
  profile: Pick<DirectSceneProfile, 'missionFamily'>,
  mission: unknown,
): boolean {
  if (typeof mission !== 'string') return false;
  const family = profile.missionFamily.trim().toLowerCase();
  const normalizedMission = mission.trim().toLowerCase();
  if (!family || !normalizedMission) return false;
  return normalizedMission === family
    || normalizedMission.startsWith(`${family}.`);
}

function isPositiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isColor(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0x000000 &&
    value <= 0xffffff
  );
}

/** Parse the small, versioned scene profile accepted from mission libraries. */
export function parseDirectSceneProfileJSON(
  profileJSON: unknown,
): DirectSceneProfile | null {
  if (
    typeof profileJSON !== 'string' ||
    utf8ByteLength(profileJSON) > DIRECT_SCENE_PROFILE_JSON_MAX_BYTES
  )
    return null;

  let value: unknown;
  try {
    value = JSON.parse(profileJSON);
  } catch (_error) {
    return null;
  }

  if (!isRecord(value) || !hasExactKeys(value, PROFILE_KEYS)) return null;
  const board = value['board'];
  const capabilities = value['capabilities'];
  if (
    value['version'] !== DIRECT_SCENE_PROFILE_VERSION ||
    !isDirectOpaqueId(value['profileId']) ||
    !isDirectOpaqueId(value['missionFamily']) ||
    !isRecord(board) ||
    !hasExactKeys(board, BOARD_KEYS) ||
    !isPositiveFinite(board['widthMm']) ||
    !isPositiveFinite(board['lengthMm']) ||
    !isPositiveFinite(board['thicknessMm']) ||
    !isColor(board['sideColor']) ||
    !isDirectMissionAssetPath(board['topTexturePath']) ||
    !isColor(board['fallbackColor']) ||
    !isRecord(capabilities) ||
    !hasExactKeys(capabilities, CAPABILITY_KEYS) ||
    !CAPABILITY_KEYS.every(
      capability => typeof capabilities[capability] === 'boolean',
    ) ||
    (value['telemetryPolicy'] !== 'backend' &&
      value['telemetryPolicy'] !== 'bootstrap-normal-finish' &&
      value['telemetryPolicy'] !== 'direct-only')
  )
    return null;

  return value as unknown as DirectSceneProfile;
}
